import { useEffect, useState } from 'react';
import { CloseIcon } from './icons';
import { inspectImage } from '../lib/cover';

/**
 * 封面上传前的确认。
 *
 * 预览是按 App 的规则渲染的——白线稿染成主题棕、彩色插画原样，垫奶油底，
 * 所以这里看到的就是它在专辑格子里的样子。上传的仍然是原始文件：
 * 染色由客户端在显示时做，把颜色烤进文件反而会让它认不出这是线稿。
 */
export function CoverDialog({ file, busy, onCancel, onConfirm }: {
  file: File;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (file: File) => void;
}) {
  const [view, setView] = useState<{ src: string; lineArt: boolean } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    let alive = true;
    setView(null);
    setError('');

    inspectImage(objectUrl)
      .then((info) => {
        if (!alive) return;
        setView({ src: info.lineArt ? info.tinted! : objectUrl, lineArt: info.lineArt });
      })
      .catch((err) => { if (alive) setError((err as Error).message); });

    return () => {
      alive = false;
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div className="modal narrow" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="close-round modal-close"
          onClick={onCancel}
          disabled={busy}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h3>Album cover</h3>
        <p className="sub">This is how the app will render it.</p>

        <div className="cover-single">
          <span className="cover-preview">
            {view && <img src={view.src} alt="" />}
          </span>
        </div>

        <p className="hint cover-filename">{file.name}</p>
        {view && (
          <p className="hint cover-note">
            {view.lineArt
              ? 'Line art — the app recolors it to the theme colour on the fly. '
                + 'The file is uploaded untouched.'
              : 'Colour artwork — the app keeps it exactly as it is.'}
          </p>
        )}
        {error && <p className="error-text">{error}</p>}

        <div className="modal-actions">
          <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => onConfirm(file)}
          >
            {busy ? 'Uploading…' : 'Upload cover'}
          </button>
        </div>
      </div>
    </div>
  );
}
